/**
 * HexCanvas — a draw.io-style pan + zoom viewport (no external deps).
 *
 * Renders `children` inside one transformed layer (translate + scale, origin 0 0).
 * Drag empty space to pan; wheel zooms toward the cursor. On touch devices one finger
 * pans and two fingers pinch-zoom toward the gesture midpoint. Drags starting on a hex
 * ([data-hex]) are ignored so clicks/taps work. Reports {tx,ty,scale} via
 * onViewportChange so the page can place screen-space overlays beside a hex
 * (screenX = tx + cx*scale, screenY = ty + cy*scale). fitToView()/resetView() via ref.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type MouseEvent as ReactMouseEvent,
} from 'react';

export interface Viewport {
  tx: number;
  ty: number;
  scale: number;
}

const MIN_SCALE = 0.25;
const MAX_SCALE = 2.5;
const MIN_VISIBLE = 140;

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

interface HexCanvasProps {
  contentWidth: number;
  contentHeight: number;
  onViewportChange?: (vp: Viewport) => void;
  children: ReactNode;
  className?: string;
  /**
   * Consulted before a pan starts; returning false vetoes it.
   *
   * The `[data-hex]` hit test below is not enough on its own for children that drag. A
   * child's `pointerdown` handler cannot stop this `mousedown` — they are separate event
   * types, so `stopPropagation()` on one has no effect on the other, and the canvas would
   * pan underneath a marker the operator is dragging. Map View sets a ref in `pointerdown`
   * (which always precedes `mousedown`) and reports it here, which makes "dragging a pin"
   * and "panning the plan" mutually exclusive by construction rather than by geometry.
   */
  canPan?: () => boolean;
  /**
   * Hold the content STILL until it's zoomed in past its fitted size.
   *
   * A floor plan is a fixed backdrop — an operator placing pins on it expects the plan to
   * stay put under their cursor, not slide away, and at fit scale the whole thing is on
   * screen so there is nothing to pan toward anyway. Panning switches on only once zoom
   * pushes content outside the viewport, where it becomes necessary rather than surprising.
   * The hexagon board leaves this off: its canvas is unbounded, so panning is the point.
   */
  panWhenZoomedOnly?: boolean;
  /**
   * What "Fit" means on this canvas.
   *
   * 'contain' (default) puts the WHOLE content on screen — right for a floor plan, which is
   * a fixed image an operator wants to see all of.
   *
   * 'width' fits the content's width and lets it run off the bottom. The honeycomb is laid
   * out TO the canvas width, so containing it would scale the board down by its height until
   * it no longer reached either edge and then centre the shortfall — leaving wide empty
   * margins on both sides of a board that was built to fill them. Height is what the
   * operator scrolls; width is what they were given.
   */
  fitMode?: 'contain' | 'width';
  /**
   * Pin "Fit" to a fixed scale instead of measuring one.
   *
   * The honeycomb board wants a KNOWN starting zoom (the level operators actually work at)
   * on load and on every Fit press, not one that drifts with the viewport size. Given, this
   * replaces the `fitMode` measurement entirely; the content is still centred the same way.
   */
  fitScale?: number;
}

export interface HexCanvasHandle {
  fitToView: () => void;
  resetView: () => void;
  /** Zoom about the viewport centre — backs on-screen +/− controls. */
  zoomBy: (factor: number) => void;
}

const HexCanvas = forwardRef<HexCanvasHandle, HexCanvasProps>(function HexCanvas(
  {
    contentWidth,
    contentHeight,
    onViewportChange,
    children,
    className = '',
    canPan,
    panWhenZoomedOnly = false,
    fitMode = 'contain',
    fitScale,
  },
  ref,
) {
  // Read through a ref so the native touch listeners below (bound once) always see the
  // caller's current predicate instead of the one captured at mount.
  const canPanRef = useRef(canPan);
  canPanRef.current = canPan;
  const containerRef = useRef<HTMLDivElement>(null);
  const [vp, setVp] = useState<Viewport>({ tx: 0, ty: 0, scale: 1 });
  const vpRef = useRef(vp);
  vpRef.current = vp;
  const hasFitRef = useRef(false);
  const dragRef = useRef<{ startX: number; startY: number; tx: number; ty: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const interactedRef = useRef(false);

  const clampTranslate = useCallback(
    (tx: number, ty: number, scale: number) => {
      const el = containerRef.current;
      if (!el) return { tx, ty };
      const vw = el.clientWidth;
      const vh = el.clientHeight;
      const cw = contentWidth * scale;
      const ch = contentHeight * scale;
      return {
        tx: clamp(tx, MIN_VISIBLE - cw, vw - MIN_VISIBLE),
        ty: clamp(ty, MIN_VISIBLE - ch, vh - MIN_VISIBLE),
      };
    },
    [contentWidth, contentHeight],
  );

  // The scale at which the whole content is on screen. `panWhenZoomedOnly` treats this as
  // the threshold: at or below it there is nothing off-screen to pan toward, so the content
  // is held still.
  const fitScaleRef = useRef(1);

  const fitToView = useCallback(() => {
    const el = containerRef.current;
    if (!el || contentWidth <= 0 || contentHeight <= 0) return;
    const vw = el.clientWidth;
    const vh = el.clientHeight;
    const scale = clamp(
      fitScale ??
        (fitMode === 'width'
          ? // Never PAST 1: a board with one group would otherwise be blown up to fill the
            // width, which is a magnifier, not a fit.
            Math.min(1, vw / contentWidth)
          : Math.min(vw / contentWidth, vh / contentHeight) * 0.96),
      MIN_SCALE,
      MAX_SCALE,
    );
    fitScaleRef.current = scale;
    const tx = (vw - contentWidth * scale) / 2;
    // Content taller than the viewport starts at the TOP — centring it would push the first
    // row off screen and hide the rest below, which is the opposite of what "Fit" promises.
    const ch = contentHeight * scale;
    const ty = ch < vh ? (vh - ch) / 2 : 0;
    setVp({ tx, ty, scale });
  }, [contentWidth, contentHeight, fitMode, fitScale]);

  /** Is the content currently held still? Only meaningful with `panWhenZoomedOnly`. */
  const panLocked = useCallback(
    () => panWhenZoomedOnly && vpRef.current.scale <= fitScaleRef.current * 1.02,
    [panWhenZoomedOnly],
  );

  // Same read-through-a-ref reason as canPanRef: the touch listeners are bound once.
  const panLockedRef = useRef(panLocked);
  panLockedRef.current = panLocked;

  const zoomBy = useCallback(
    (factor: number) => {
      const el = containerRef.current;
      if (!el) return;
      interactedRef.current = true;
      const cur = vpRef.current;
      const next = clamp(cur.scale * factor, MIN_SCALE, MAX_SCALE);
      if (next === cur.scale) return;
      // Zoom about the viewport centre, the same maths the wheel uses about the cursor.
      const mx = el.clientWidth / 2;
      const my = el.clientHeight / 2;
      const ntx = mx - (mx - cur.tx) * (next / cur.scale);
      const nty = my - (my - cur.ty) * (next / cur.scale);
      const c = clampTranslate(ntx, nty, next);
      setVp({ tx: c.tx, ty: c.ty, scale: next });
    },
    [clampTranslate],
  );

  useImperativeHandle(ref, () => ({ fitToView, resetView: fitToView, zoomBy }), [fitToView, zoomBy]);

  useLayoutEffect(() => {
    if (!hasFitRef.current && contentWidth > 0 && contentHeight > 0 && containerRef.current?.clientWidth) {
      fitToView();
      hasFitRef.current = true;
    }
  }, [contentWidth, contentHeight, fitToView]);

  useEffect(() => {
    onViewportChange?.(vp);
  }, [vp, onViewportChange]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      if (!interactedRef.current) fitToView();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fitToView]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      interactedRef.current = true;
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const cur = vpRef.current;
      const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
      const next = clamp(cur.scale * factor, MIN_SCALE, MAX_SCALE);
      if (next === cur.scale) return;
      const ntx = mx - (mx - cur.tx) * (next / cur.scale);
      const nty = my - (my - cur.ty) * (next / cur.scale);
      const c = clampTranslate(ntx, nty, next);
      setVp({ tx: c.tx, ty: c.ty, scale: next });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [clampTranslate]);

  // Touch: one finger pans, two fingers pinch-zoom toward the gesture midpoint.
  // Native listeners (passive: false on move) so we can preventDefault page scrolling
  // only while a gesture is actually panning/zooming — taps on hexes still click.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let pan: { x: number; y: number; tx: number; ty: number } | null = null;
    let pinch: { dist: number; cx: number; cy: number; scale: number; tx: number; ty: number } | null = null;

    const pt = (t: Touch) => {
      const r = el.getBoundingClientRect();
      return { x: t.clientX - r.left, y: t.clientY - r.top };
    };
    const dist = (a: Touch, b: Touch) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length >= 2) {
        interactedRef.current = true;
        const [a, b] = [e.touches[0], e.touches[1]];
        const pa = pt(a);
        const pb = pt(b);
        pinch = {
          dist: dist(a, b),
          cx: (pa.x + pb.x) / 2,
          cy: (pa.y + pb.y) / 2,
          scale: vpRef.current.scale,
          tx: vpRef.current.tx,
          ty: vpRef.current.ty,
        };
        pan = null;
      } else if (e.touches.length === 1) {
        // A touch starting on a hex is a tap (click), not a pan.
        if ((e.target as HTMLElement).closest('[data-hex]')) return;
        if (canPanRef.current && !canPanRef.current()) return;
        if (panLockedRef.current()) return;
        interactedRef.current = true;
        const p = pt(e.touches[0]);
        pan = { x: p.x, y: p.y, tx: vpRef.current.tx, ty: vpRef.current.ty };
        pinch = null;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (pinch && e.touches.length >= 2) {
        e.preventDefault();
        const [a, b] = [e.touches[0], e.touches[1]];
        const pa = pt(a);
        const pb = pt(b);
        const next = clamp(pinch.scale * (dist(a, b) / pinch.dist), MIN_SCALE, MAX_SCALE);
        const mx = (pa.x + pb.x) / 2;
        const my = (pa.y + pb.y) / 2;
        // Zoom toward the original midpoint, then follow the midpoint drift (pan).
        const ntx = pinch.cx - (pinch.cx - pinch.tx) * (next / pinch.scale) + (mx - pinch.cx);
        const nty = pinch.cy - (pinch.cy - pinch.ty) * (next / pinch.scale) + (my - pinch.cy);
        const c = clampTranslate(ntx, nty, next);
        setVp({ tx: c.tx, ty: c.ty, scale: next });
      } else if (pan && e.touches.length === 1) {
        e.preventDefault();
        const p = pt(e.touches[0]);
        const c = clampTranslate(pan.tx + (p.x - pan.x), pan.ty + (p.y - pan.y), vpRef.current.scale);
        setVp((v) => ({ ...v, tx: c.tx, ty: c.ty }));
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length === 0) {
        pan = null;
        pinch = null;
      } else if (e.touches.length === 1) {
        // Pinch ended with one finger still down — continue as a pan from here.
        pinch = null;
        const p = pt(e.touches[0]);
        pan = { x: p.x, y: p.y, tx: vpRef.current.tx, ty: vpRef.current.ty };
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [clampTranslate]);

  const onMouseDown = (e: ReactMouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-hex]')) return;
    if (canPanRef.current && !canPanRef.current()) return;
    if (panLocked()) return;
    if (e.button !== 0) return;
    interactedRef.current = true;
    dragRef.current = { startX: e.clientX, startY: e.clientY, tx: vp.tx, ty: vp.ty };
    setDragging(true);
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const cur = vpRef.current;
      const c = clampTranslate(d.tx + (e.clientX - d.startX), d.ty + (e.clientY - d.startY), cur.scale);
      setVp((v) => ({ ...v, tx: c.tx, ty: c.ty }));
    };
    const onUp = () => {
      dragRef.current = null;
      setDragging(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging, clampTranslate]);

  return (
    <div
      ref={containerRef}
      onMouseDown={onMouseDown}
      className={`relative h-full w-full touch-none overflow-hidden ${
        panWhenZoomedOnly && vp.scale <= fitScaleRef.current * 1.02
          ? 'cursor-default'
          : dragging
            ? 'cursor-grabbing'
            : 'cursor-grab'
      } ${className}`}
    >
      <div
        className="absolute left-0 top-0 origin-top-left will-change-transform"
        style={{
          transform: `translate(${vp.tx}px, ${vp.ty}px) scale(${vp.scale})`,
          width: contentWidth,
          height: contentHeight,
        }}
      >
        {children}
      </div>
    </div>
  );
});

export default HexCanvas;
